import { Component, type ErrorInfo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useOptionalTenantContext } from "@/hooks/useTenantContext";
import { logErrorTelemetry } from "@/hooks/useErrorTelemetry";
import * as Sentry from "@sentry/react";
import { useActionTelemetry } from "@/hooks/useActionTelemetry";
import {
  getAuthRedirectUrl,
  isEdgeFunctionAuthError,
  invokeEdgeFunction,
} from "@/lib/edge-functions";
import { ForgeLogo } from "@/components/ForgeLogo";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { logger } from "@/lib/logger";
import { trackUserAction } from "@/lib/auth-transition-logger";
import { buildErrorDetails } from "@/lib/error-handling";


/**
 * Check if an error is a session verification error (thrown by ProtectedRoute
 * when session fetch fails due to network/server issues).
 * These errors should NOT prompt the user to sign in again.
 */
function isSessionVerificationError(error: Error): boolean {
  return error.name === "SessionVerificationError";
}

/**
 * Check if an error is a page/render error that should NOT redirect to auth.
 * These are JS runtime errors, React render errors, etc.
 */
function isPageError(error: Error): boolean {
  // Session verification errors are network/server issues, not auth issues
  if (isSessionVerificationError(error)) return true;

  // Common JS runtime errors
  if (error instanceof TypeError || error instanceof ReferenceError) {
    return true;
  }

  // React/rendering errors (check error message patterns)
  const pageErrorPatterns = [
    "chunk",
    "module",
    "render",
    "component",
    "cannot read",
    "is not a function",
    "is not defined",
    "hydration",
    "must be used within",  // React context provider boundary errors
  ];

  const message = error.message.toLowerCase();
  return pageErrorPatterns.some((pattern) => message.includes(pattern));
}

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

type ErrorBoundaryProps = {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  renderFallback: (props: {
    error: Error;
    errorInfo: ErrorInfo | null;
    reset: () => void;
  }) => ReactNode;
};

class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    // Send to Sentry with the React component stack as a tag so triage
    // can find the failing component without re-running locally.
    // `initMonitoring` no-ops when VITE_SENTRY_DSN is unset, so this is
    // safe to call unconditionally in dev / test.
    Sentry.withScope((scope) => {
      scope.setTag("error_boundary", "global");
      scope.setContext("react", {
        componentStack: errorInfo.componentStack ?? null,
      });
      Sentry.captureException(error);
    });
    this.props.onError?.(error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    if (hasError && error) {
      return this.props.renderFallback({ error, errorInfo, reset: this.reset });
    }
    return this.props.children;
  }
}

const createDiagnosticsText = (payload: {
  route: string;
  roleSummary: string;
  orgId?: string | null;
  userId?: string | null;
  error: Error;
  errorInfo: ErrorInfo | null;
}) => {
  return [
    "ForgeHQ client error report",
    `Timestamp: ${new Date().toISOString()}`,
    `Route: ${payload.route}`,
    `Roles: ${payload.roleSummary || "unknown"}`,
    `Organization: ${payload.orgId ?? "unknown"}`,
    `User: ${payload.userId ?? "unknown"}`,
    `Error: ${payload.error.message}`,
    payload.error.stack ? `Stack: ${payload.error.stack}` : null,
    payload.errorInfo?.componentStack ? `Component stack: ${payload.errorInfo.componentStack}` : null,
  ]
    .filter(Boolean)
    .join("\n");
};

function GlobalErrorFallback({
  error,
  errorInfo,
  route,
  roleSummary,
  orgId,
  userId,
  onRetryWithRehydration,
  reset,
}: {
  error: Error;
  errorInfo: ErrorInfo | null;
  route: string;
  roleSummary: string;
  orgId?: string | null;
  userId?: string | null;
  onRetryWithRehydration: () => Promise<void>;
  reset: () => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [reportNote, setReportNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);


  // Determine error type for appropriate UI
  const isSessionError = isSessionVerificationError(error);
  const isPageRenderError = isPageError(error);
  const isAuthError = isEdgeFunctionAuthError(error);
  const errorDetails = useMemo(() => buildErrorDetails(error), [error]);

  // Only show "Sign in again" for actual auth errors, NOT for page/session errors
  const showSignInButton = isAuthError && !isSessionError && !isPageRenderError;

  const diagnostics = useMemo(
    () =>
      createDiagnosticsText({
        route,
        roleSummary,
        orgId,
        userId,
        error,
        errorInfo,
      }),
    [error, errorInfo, orgId, roleSummary, route, userId],
  );

  const authRedirect = isAuthError ? error.redirectTo : getAuthRedirectUrl();


  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(diagnostics);
    } catch (clipboardError) {
      console.error("Failed to copy diagnostics:", clipboardError);
    }
  };

  /**
   * Handle retry with session rehydration.
   * This attempts to restore the session before navigating, preventing
   * the user from being incorrectly redirected to /auth.
   */
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    trackUserAction("error_boundary_retry");

    try {
      logger.info("[GlobalErrorBoundary] Attempting recovery with session rehydration");
      await onRetryWithRehydration();
      // Reload the page after rehydration to reset the error boundary
      window.location.reload();
    } catch (retryError) {
      logger.error("[GlobalErrorBoundary] Recovery failed:", retryError);
      toast({
        title: "Unable to recover",
        description: "Please try reloading the page.",
        variant: "destructive",
      });
    } finally {
      setIsRetrying(false);
    }
  }, [onRetryWithRehydration, toast]);

  const handleGoBack = useCallback(async () => {
    trackUserAction("error_boundary_go_back");

    // Attempt session rehydration before navigating
    try {
      await onRetryWithRehydration();
    } catch {
      // Continue anyway - navigation may still work
    }

    const previousRoute = sessionStorage.getItem("previous_route");
    const blockedPrefixes = ["/auth", "/reset-password", "/accept-invite", "/mfa", "/onboarding", "/account-disabled"];
    if (previousRoute && previousRoute !== route && !blockedPrefixes.some(prefix => previousRoute.startsWith(prefix))) {
      reset();
      navigate(previousRoute, { replace: true });
      return;
    }
    reset();
    navigate("/", { replace: true });
  }, [navigate, onRetryWithRehydration, reset, route]);

  const handleReload = useCallback(async () => {
    trackUserAction("error_boundary_reload");

    // Attempt session rehydration before reloading
    try {
      await onRetryWithRehydration();
    } catch {
      // Continue anyway - reload will trigger fresh session fetch
    }

    window.location.reload();
  }, [onRetryWithRehydration]);

  const submitReport = async () => {
    setIsSubmitting(true);
    try {
      try {
        await onRetryWithRehydration();
      } catch {
        // Continue anyway - error reports can be sent without a rehydrated session
      }
      const { error: submitError } = await invokeEdgeFunction("submit-error-report", {
        body: {
          diagnostics,
          route,
          roleSummary,
          errorMessage: error.message,
          userMessage: reportNote.trim() || null,
          organizationId: orgId ?? null,
        },
        policy: "interactive",
      });

      if (submitError) {
        throw submitError;
      }

      toast({ title: "Report sent", description: "Support has received your report." });
      setReportNote("");
      setOpen(false);
    } catch (submitError) {
      console.error("Failed to submit error report:", submitError);
      toast({
        title: "Unable to send report",
        description: submitError instanceof Error ? submitError.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showRetryButton = isSessionError || errorDetails.isTransient;

  // Customize messaging based on error type
  const errorTitle = isSessionError
    ? "Connection issue"
    : errorDetails.category === "permission"
      ? "Access restricted"
      : errorDetails.category === "server"
        ? "Server issue"
        : errorDetails.category === "network"
          ? "Connection issue"
          : "Something went off course";

  const errorDescription = isSessionError
    ? "We had trouble verifying your session. This is usually temporary - try again in a moment."
    : errorDetails.category === "unknown"
      ? `${errorDetails.userMessage} Retry or contact support for help.`
      : errorDetails.userMessage;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-6 py-12">
      <div className="w-full max-w-xl space-y-6 rounded-2xl border border-border/60 bg-background p-8 shadow-sm">
        {/* Logo with warning overlay */}
        <div className="relative mx-auto w-fit">
          <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-muted/50">
            <ForgeLogo size="lg" className="opacity-60" />
          </div>
          <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-warning/10 border-2 border-background">
            <AlertTriangle className="h-3.5 w-3.5 text-warning" />
          </div>
        </div>

        <div className="space-y-2 text-center">
          <h1 className="text-xl font-semibold text-foreground">{errorTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {errorDescription}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {/* For session/transient errors, show a "Retry" button that rehydrates the session */}
          {showRetryButton && (
            <Button onClick={handleRetry} disabled={isRetrying}>
              {isRetrying ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Retrying...
                </>
              ) : (
                "Retry"
              )}
            </Button>
          )}
          {!showRetryButton && <Button onClick={handleReload}>Reload page</Button>}
          <Button variant="outline" onClick={handleGoBack}>Go back</Button>
          {/* Only show "Sign in again" for actual auth errors, not page/session errors */}
          {showSignInButton && (
            <Button variant="ghost" onClick={() => window.location.assign(authRedirect)}>
              Sign in again
            </Button>
          )}
        </div>
        <div className="rounded-lg border border-border/50 bg-muted/30 p-4 text-sm text-muted-foreground text-center">
          If this keeps happening,{" "}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-primary underline-offset-4 hover:underline"
          >
            share a quick report
          </button>{" "}
          with support.
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Report this issue</DialogTitle>
            <DialogDescription>
              Share the details below with support. You can keep browsing after closing this window.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Add any context that could help support reproduce the issue.</p>
            <Textarea
              value={reportNote}
              onChange={(event) => setReportNote(event.target.value)}
              placeholder="What were you doing when this happened?"
              className="min-h-[120px]"
            />
          </div>
          <Textarea value={diagnostics} readOnly className="min-h-[220px] font-mono text-xs" />
          <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
            <Button variant="outline" onClick={copyDiagnostics}>Copy diagnostics</Button>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>Done</Button>
              <Button onClick={submitReport} disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send to support"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function GlobalErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, rehydrateSession } = useAuth();
  // NOTE: This boundary wraps public routes too, so it MUST remain context-independent.
  // Do not use hooks that depend on OrganizationProvider (e.g., useOrganization/useUserRoles).
  const tenantContext = useOptionalTenantContext()?.tenantContext ?? null;
  const { trackError } = useActionTelemetry();
  const [errorState, setErrorState] = useState<{ error: Error; errorInfo: ErrorInfo } | null>(null);
  const lastRouteRef = useRef<string | null>(null);

  // Cannot use useUserRoles here (above OrganizationProvider), so provide a safe fallback
  const roleSummary = "unavailable";

  const route = useMemo(
    () => `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`,
    [location?.hash, location?.pathname, location?.search],
  );

  useEffect(() => {
    const lastRoute = lastRouteRef.current;
    if (lastRoute && lastRoute !== route) {
      sessionStorage.setItem("previous_route", lastRoute);
    }
    sessionStorage.setItem("last_route", route);
    lastRouteRef.current = route;
  }, [route]);

  // Log errors to database and console
  useEffect(() => {
    if (!errorState) return;

    // Determine error category for better logging
    const isSessionError = errorState.error.name === "SessionVerificationError";
    const errorDetails = buildErrorDetails(errorState.error);
    const errorCategory = isSessionError ? "session_verification" : errorDetails.category;

    // Log to console for debugging
    logger.error("[GlobalErrorBoundary] Error captured:", {
      route,
      userId: user?.id ?? null,
      roleSummary,
      errorMessage: errorState.error.message,
      status: errorDetails.status,
      errorCategory,
      stack: errorState.error.stack ?? null,
      rawError: errorState.error,
    });

    // Log to error_telemetry table via RPC function (fire-and-forget)
    logErrorTelemetry({
      errorMessage: errorState.error.message,
      errorName: errorState.error.name,
      route,
      errorStack: errorState.error.stack ?? null,
      componentStack: errorState.errorInfo?.componentStack ?? null,
      roleSummary,
      metadata: {
        userId: user?.id ?? null,
        errorCategory,
        // Flag that this was a session error, NOT an auth redirect
        isSessionError,
      },
    });

    // Also log to platform_events so errors appear in the Activity tab
    trackError(errorState.error, {
      route,
      errorCategory,
      isSessionError,
    });
  }, [errorState, roleSummary, route, user?.id, trackError]);

  /**
   * Callback for error recovery that rehydrates the session.
   * This is passed to the fallback UI so it can attempt to restore
   * the session before reloading or navigating.
   */
  const handleRetryWithRehydration = useCallback(async () => {
    logger.info("[GlobalErrorBoundary] Attempting session rehydration for recovery");
    await rehydrateSession();
  }, [rehydrateSession]);

  return (
    <AppErrorBoundary
      onError={(error, errorInfo) => setErrorState({ error, errorInfo })}
      renderFallback={({ error, errorInfo, reset }) => (
        <GlobalErrorFallback
          error={error}
          errorInfo={errorInfo}
          route={route}
          roleSummary={roleSummary}
          orgId={tenantContext?.organizationId ?? null}
          userId={user?.id ?? null}
          onRetryWithRehydration={handleRetryWithRehydration}
          reset={reset}
        />
      )}
    >
      {children}
    </AppErrorBoundary>
  );
}
